import styles from "@fyendalscollection/marketing/CardCanvas.module.scss";
import cardBack from "@fyendalscollection/marketing/assets/cardBack.webp";
import { useCallback, useEffect, useRef, useState } from "react";

export const CardCanvas = (): JSX.Element => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animationReference = useRef<number | null>(null);
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);

	const animate = useCallback((time: DOMHighResTimeStamp) => {
		if (!canvasRef.current) {
			return;
		}

		const context = canvasRef.current.getContext("2d");
		if (!context) {
			return;
		}

		context.clearRect(0, 0, width, height);

		const cardWidth = 192;
		const cardHeight = 268;
		const padding = 64;
		const columns = Math.ceil(width / (cardWidth + padding)) + 1;
		const rows = Math.ceil(height / (cardHeight + padding)) + 2;

		for (let i = 0; i < columns; i++) {
			for (let j = 0; j < rows; j++) {
				const x = i * (cardWidth + padding) - cardWidth / 2;
				const y = (j - 2) * (cardHeight + padding) + padding + (time / 40) % (cardHeight + padding) + (i % 2 === 0 ? (cardHeight + padding) / 2 : 0);

				context.drawImage(cardBackImage, x, y, cardWidth, cardHeight);
			}
		}

		const gradient = context.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height));
		gradient.addColorStop(0.15, "rgba(255, 255, 255, 0)");
		gradient.addColorStop(1, "rgba(255, 255, 255, 1)");
		context.globalCompositeOperation = "destination-in";
		context.fillStyle = gradient;
		context.fillRect(0, 0, width, height);
		context.globalCompositeOperation = "source-over";
	}, [width, height]);

	useEffect(() => {
		const callback = () => {
			if (!canvasRef.current) {
				return;
			}

			setWidth(canvasRef.current.clientWidth);
			setHeight(canvasRef.current.clientHeight);
		};

		callback();
		window.addEventListener("resize", callback);
		return () => window.removeEventListener("resize", callback);
	}, []);

	useEffect(() => {
		const callback = (time: DOMHighResTimeStamp) => {
			animate(time);
			animationReference.current = requestAnimationFrame(callback);
		};

		animationReference.current = requestAnimationFrame(callback);

		return () => {
			if (!animationReference.current) {
				return;
			}

			cancelAnimationFrame(animationReference.current);
		};
	}, [animate]);

	return (
		<canvas ref={canvasRef} width={width} height={height} className={styles.cardCanvas}></canvas>
	);
};

const cardBackImage = new Image();
cardBackImage.src = cardBack;
